import React, { useEffect, useState } from "react";
import "./MyProfile.css";
import { useAuth } from "../context/AuthContext";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { genders } from "../../utils/constants";
import {
  getLinkedPatients,
  selectPatient,
  updatePatient,
} from "../../api/patient";
import useAppContext from "../context/AppContext";
import PatientAvatar, { patientAgeYears } from "../PatientAvatar/PatientAvatar";

/**
 * The patient's own record, shown the way the clinic holds it.
 *
 * It used to show five fields — name, phone, email, date of birth, gender —
 * while the clinic's copy of the same record carries an address, an attendant,
 * an ABHA link and a UHID the patient is asked for at the desk. Everything the
 * patient can usefully see is here now; what only the clinic may change is
 * shown read-only rather than hidden, so the patient can spot a wrong address
 * and say so.
 *
 * There is no back button: the app bar is the navigation, and this screen is
 * one of its tabs.
 */

const AREA_TYPES = ["URBAN", "RURAL"];

const EMPTY = "—";

/** "12 MG Road, Kothrud, Pune, Maharashtra 411001" from the address block. */
export function formatAddress(address) {
  if (!address) return "";
  return [
    address.address1,
    address.address2,
    address.cityVillage,
    address.taluka,
    address.district,
    [address.state, address.pincode].filter(Boolean).join(" ").trim(),
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

/** What the record says about age, however it says it. */
export function formatAge(patient) {
  const years = patientAgeYears(patient);
  if (years == null) return String(patient?.age || "").trim();
  return years === 0 ? "Under 1 yr" : `${years} yrs`;
}

/**
 * One line describing a record in the picker: "32 yrs · 19 Aug 1994 · Male ·
 * 8808038753". Mirrors the doctor app's patient card, because the records this
 * login can reach share a phone and very often a name too.
 */
export function recordSummary(record) {
  const gender = record?.gender
    ? record.gender.charAt(0) + record.gender.slice(1).toLowerCase()
    : "";
  return [
    formatAge(record),
    // The desk-created twin has an age and no date of birth; the portal one has
    // both. Showing whichever it holds is what tells them apart.
    record?.dateOfBirth ? moment(record.dateOfBirth).format("DD MMM YYYY") : "",
    gender,
    record?.phone,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Only what the patient actually changed, and never a blank over a value the
 * clinic holds. The API writes exactly the keys it is sent.
 */
export function buildProfileUpdate(form, user) {
  const payload = {
    name: String(form.name || "").trim(),
    ethnicity: String(form.ethnicity || "").trim(),
    address: {
      address1: String(form.address?.address1 || "").trim(),
      address2: String(form.address?.address2 || "").trim(),
      cityVillage: String(form.address?.cityVillage || "").trim(),
      taluka: String(form.address?.taluka || "").trim(),
      district: String(form.address?.district || "").trim(),
      state: String(form.address?.state || "").trim(),
      pincode: String(form.address?.pincode || "").trim(),
      areaType: form.address?.areaType || "",
    },
    attendantDetails: {
      name: String(form.attendantDetails?.name || "").trim(),
      relationship: String(form.attendantDetails?.relationship || "").trim(),
      contactNumber: String(form.attendantDetails?.contactNumber || "").trim(),
    },
  };

  // Sent only when there is something to say: a desk-created record often has
  // no email, no date of birth and no gender, and an empty string or a null
  // would overwrite the clinic's copy with nothing.
  const email = String(form.email || "").trim();
  if (email || user?.email) payload.email = email;
  if (form.dateOfBirth) payload.dateOfBirth = form.dateOfBirth;
  if (form.gender) payload.gender = form.gender;

  return payload;
}

const formFromUser = (user) => ({
  name: user?.name || "",
  email: user?.email || "",
  dateOfBirth: user?.dateOfBirth
    ? moment(user.dateOfBirth).format("YYYY-MM-DD")
    : "",
  gender: user?.gender || "",
  ethnicity: user?.ethnicity || "",
  address: {
    address1: user?.address?.address1 || "",
    address2: user?.address?.address2 || "",
    cityVillage: user?.address?.cityVillage || "",
    taluka: user?.address?.taluka || "",
    district: user?.address?.district || "",
    state: user?.address?.state || "",
    pincode: user?.address?.pincode || "",
    areaType: user?.address?.areaType || "",
  },
  attendantDetails: {
    name: user?.attendantDetails?.name || "",
    relationship: user?.attendantDetails?.relationship || "",
    contactNumber: user?.attendantDetails?.contactNumber || "",
  },
});

const MyProfile = () => {
  const { logout, user, setUser, setToken } = useAuth();
  const { openSnackbar } = useAppContext();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [form, setForm] = useState(() => formFromUser(user));

  // One phone or email can reach more than one patient record — the one the
  // clinic registered at the desk and the self-registered portal one — and each
  // keeps its own appointments. Offer the choice only when there is one to make.
  const [linkedPatients, setLinkedPatients] = useState([]);
  const [switchingTo, setSwitchingTo] = useState("");

  useEffect(() => {
    let active = true;
    getLinkedPatients().then((records) => {
      if (active) setLinkedPatients(records || []);
    });
    return () => {
      active = false;
    };
  }, [user?._id]);

  async function onSwitchPatient(patientId) {
    if (!patientId || switchingTo) return;
    setSwitchingTo(patientId);
    const response = await selectPatient(patientId);
    setSwitchingTo("");

    if (response?.status === 200 && response?.data?.token) {
      // Setting the token re-runs the auth effect, which re-reads the profile
      // and repaints the whole app as the chosen person.
      setToken(response.data.token);
      openSnackbar({
        severity: "success",
        message: `Now viewing ${response.data?.data?.name || "the selected record"}`,
      });
      navigate("/bookings");
      return;
    }

    openSnackbar({
      severity: "error",
      message: response?.data?.message || "Could not switch patient record",
    });
  }

  function onStartEdit() {
    setForm(formFromUser(user));
    setIsEditing(true);
  }

  function onCancelEdit() {
    setForm(formFromUser(user));
    setIsEditing(false);
  }

  /** `set("address.pincode", value)` — the payload is nested, the inputs are flat. */
  function set(path, value) {
    const [head, tail] = path.split(".");
    setForm((prev) =>
      tail
        ? { ...prev, [head]: { ...prev[head], [tail]: value } }
        : { ...prev, [head]: value },
    );
  }

  async function onClickUpdate() {
    setLoadingUpdate(true);
    const updated = await updatePatient(buildProfileUpdate(form, user));
    setLoadingUpdate(false);

    if (updated) {
      setUser(updated);
      setIsEditing(false);
      openSnackbar({ severity: "success", message: "Profile updated" });
      return;
    }
    openSnackbar({
      severity: "error",
      message: "Could not update your profile. Please try again.",
    });
  }

  /* Rendered as plain functions, not inner components: a component defined
     during render is a new type on every keystroke, which remounts the input
     and loses the caret. */

  const readRow = (label, value) => (
    <div className="profile_row" key={label}>
      <span className="profile_row_label">{label}</span>
      <span
        className={value ? "profile_row_value" : "profile_row_value is_empty"}
      >
        {value || EMPTY}
      </span>
    </div>
  );

  const inputRow = (label, path, value, extra = {}) => (
    <div className="profile_field" key={path}>
      <label htmlFor={`profile-${path}`}>{label}</label>
      <input
        id={`profile-${path}`}
        name={path}
        value={value}
        onChange={(e) => set(path, e.target.value)}
        {...extra}
      />
    </div>
  );

  const selectRow = (label, path, value, options) => (
    <div className="profile_field" key={path}>
      <label htmlFor={`profile-${path}`}>{label}</label>
      <select
        id={`profile-${path}`}
        name={path}
        value={value}
        onChange={(e) => set(path, e.target.value)}
      >
        <option value="">Not specified</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option.charAt(0) + option.slice(1).toLowerCase()}
          </option>
        ))}
      </select>
    </div>
  );

  const genderLabel = user?.gender
    ? user.gender.charAt(0) + user.gender.slice(1).toLowerCase()
    : "";
  const age = formatAge(user);
  const abhaAddress =
    user?.abhaAddress || (user?.healthId?.length ? user.healthId[0] : "");

  return (
    <div id="layout" className="profile_page">
      <div className="profile_section profile_identity">
        <PatientAvatar patient={user} size={64} />
        <div className="profile_identity_text">
          <div className="profile_identity_name">
            {user?.name || "Your profile"}
          </div>
          <div className="profile_identity_uhid">
            UHID {user?.patientId || EMPTY}
          </div>
          <div className="profile_chips">
            {genderLabel && <span className="profile_chip">{genderLabel}</span>}
            {age && <span className="profile_chip">{age}</span>}
            {user?.phone && (
              <span className="profile_chip">
                {user.phone}
                {user?.phoneVerified ? " ✓" : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      {linkedPatients.length > 1 && (
        <div className="profile_section linked_patients">
          <div className="linked_patients_title">Viewing records for</div>
          <div className="linked_patients_hint">
            Your mobile number or email is on more than one patient record. Each
            keeps its own appointments and prescriptions — pick the one you want
            to see.
          </div>
          {linkedPatients.map((record) => (
            <button
              key={record._id}
              type="button"
              className={
                record.isCurrent
                  ? "linked_patient_option is_current"
                  : "linked_patient_option"
              }
              disabled={record.isCurrent || Boolean(switchingTo)}
              onClick={() => onSwitchPatient(record._id)}
            >
              <PatientAvatar patient={record} size={44} />
              <span className="linked_patient_text">
                <span className="linked_patient_name">
                  {record.name || "Unnamed record"}
                </span>
                {/* Records on one phone routinely share a name — the desk
                    types it again rather than loading the existing record —
                    so the row carries the same details as the doctor app's
                    patient card. */}
                <span className="linked_patient_meta">
                  {recordSummary(record)}
                </span>
                <span className="linked_patient_badges">
                  {record.patientId && (
                    <span className="linked_badge linked_badge_uhid">
                      UHID {record.patientId}
                    </span>
                  )}
                  {record.hasAadhaar && (
                    <span className="linked_badge linked_badge_aadhaar">
                      Aadhaar
                    </span>
                  )}
                  {record.hasAbha && (
                    <span className="linked_badge linked_badge_abha">ABHA</span>
                  )}
                  {record.phoneVerified && (
                    <span className="linked_badge linked_badge_verified">
                      Mobile verified
                    </span>
                  )}
                </span>
                {record.registeredAt && (
                  <span className="linked_patient_meta">
                    Registered{" "}
                    {moment(record.registeredAt).format("DD MMM YYYY")}
                  </span>
                )}
              </span>
              <span className="linked_patient_action">
                {record.isCurrent
                  ? "Viewing"
                  : switchingTo === record._id
                    ? "Switching…"
                    : "Switch"}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="profile_section">
        <div className="profile_section_head">
          <h2 className="profile_section_title">Personal details</h2>
          <button
            type="button"
            className="profile_edit_btn"
            onClick={isEditing ? onCancelEdit : onStartEdit}
          >
            {isEditing ? "Cancel" : "Edit details"}
          </button>
        </div>

        {isEditing ? (
          <>
            {inputRow("Full name", "name", form.name, {
              type: "text",
              autoComplete: "name",
            })}
            {inputRow("Date of birth", "dateOfBirth", form.dateOfBirth, {
              type: "date",
              max: moment().format("YYYY-MM-DD"),
            })}
            <div className="profile_field">
              <label htmlFor="profile-gender">Gender</label>
              <div className="profile_choices" id="profile-gender">
                {genders.map((gender) => (
                  <button
                    key={gender}
                    type="button"
                    className={
                      form.gender === gender
                        ? "profile_choice is_selected"
                        : "profile_choice"
                    }
                    onClick={() => set("gender", gender)}
                  >
                    {gender.charAt(0) + gender.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>
            {inputRow("Ethnicity", "ethnicity", form.ethnicity, {
              type: "text",
              placeholder: "Optional",
            })}
          </>
        ) : (
          <>
            {readRow("Full name", user?.name)}
            {readRow(
              "Date of birth",
              user?.dateOfBirth
                ? moment(user.dateOfBirth).format("DD MMM YYYY")
                : "",
            )}
            {readRow("Age", age)}
            {readRow("Gender", genderLabel)}
            {readRow("Ethnicity", user?.ethnicity)}
          </>
        )}
      </div>

      <div className="profile_section">
        <h2 className="profile_section_title">Contact details</h2>
        {/* The mobile number is the login and the key the clinic finds the
            patient by — it is changed at the desk, never here. */}
        {readRow(
          "Mobile number",
          user?.phone
            ? `${user.phone}${user?.phoneVerified ? " (verified)" : ""}`
            : "",
        )}
        {isEditing ? (
          <>
            {inputRow("Email", "email", form.email, {
              type: "email",
              autoComplete: "email",
              placeholder: "Optional",
            })}
            {inputRow("Address", "address.address1", form.address.address1, {
              type: "text",
            })}
            {inputRow(
              "Area / locality",
              "address.address2",
              form.address.address2,
              { type: "text", placeholder: "Optional" },
            )}
            {inputRow(
              "City / village",
              "address.cityVillage",
              form.address.cityVillage,
              { type: "text" },
            )}
            {inputRow("Taluka", "address.taluka", form.address.taluka, {
              type: "text",
              placeholder: "Optional",
            })}
            {inputRow("District", "address.district", form.address.district, {
              type: "text",
            })}
            {inputRow("State", "address.state", form.address.state, {
              type: "text",
            })}
            {inputRow("Pincode", "address.pincode", form.address.pincode, {
              type: "text",
              inputMode: "numeric",
              maxLength: 6,
            })}
            {selectRow(
              "Area type",
              "address.areaType",
              form.address.areaType,
              AREA_TYPES,
            )}
          </>
        ) : (
          <>
            {readRow("Email", user?.email)}
            {readRow("Address", formatAddress(user?.address))}
            {readRow(
              "Area type",
              user?.address?.areaType
                ? user.address.areaType.charAt(0) +
                    user.address.areaType.slice(1).toLowerCase()
                : "",
            )}
          </>
        )}
      </div>

      <div className="profile_section">
        <h2 className="profile_section_title">Emergency contact</h2>
        <div className="profile_section_hint">
          Who the clinic should call if you cannot be reached.
        </div>
        {isEditing ? (
          <>
            {inputRow(
              "Name",
              "attendantDetails.name",
              form.attendantDetails.name,
              {
                type: "text",
              },
            )}
            {inputRow(
              "Relationship",
              "attendantDetails.relationship",
              form.attendantDetails.relationship,
              { type: "text", placeholder: "Spouse, parent, friend…" },
            )}
            {inputRow(
              "Contact number",
              "attendantDetails.contactNumber",
              form.attendantDetails.contactNumber,
              { type: "tel", inputMode: "numeric", maxLength: 15 },
            )}
          </>
        ) : (
          <>
            {readRow("Name", user?.attendantDetails?.name)}
            {readRow("Relationship", user?.attendantDetails?.relationship)}
            {readRow("Contact number", user?.attendantDetails?.contactNumber)}
          </>
        )}
      </div>

      {/* ABHA is issued and verified by ABDM — shown, never edited here. */}
      <div className="profile_section">
        <h2 className="profile_section_title">ABHA (health ID)</h2>
        {user?.healthIdNumber || abhaAddress ? (
          <>
            {readRow("ABHA number", user?.healthIdNumber)}
            {readRow("ABHA address", abhaAddress)}
          </>
        ) : (
          <div className="profile_section_hint">
            No ABHA linked to this record yet.
          </div>
        )}
      </div>

      <div className="profile_section">
        <h2 className="profile_section_title">Registration</h2>
        {readRow("Patient ID (UHID)", user?.patientId)}
        {readRow(
          "Registered on",
          user?.registeredAt
            ? moment(user.registeredAt).format("DD MMM YYYY")
            : "",
        )}
        {user?.memberId ? readRow("Member ID", user.memberId) : null}
      </div>

      {isEditing && (
        <div className="profile_section profile_actions profile_save_bar">
          <button
            type="button"
            className="profile_primary_btn"
            onClick={onClickUpdate}
            disabled={loadingUpdate}
          >
            {loadingUpdate ? (
              <CircularProgress sx={{ color: "white" }} size="1.25rem" />
            ) : (
              "Save changes"
            )}
          </button>
        </div>
      )}

      <div className="profile_section profile_menu">
        <div className="profile_menu_title">Help &amp; support</div>
        <button
          type="button"
          className="profile_menu_item"
          onClick={() => navigate("/feedback")}
        >
          <span>
            <span className="profile_menu_label">
              Feedback &amp; complaints
            </span>
            <span className="profile_menu_hint">
              Rate a visit or raise a complaint with your clinic
            </span>
          </span>
          <span className="profile_menu_chevron" aria-hidden="true">
            ›
          </span>
        </button>
        <button
          type="button"
          className="profile_menu_item"
          onClick={() => navigate("/termsandcondition")}
        >
          <span className="profile_menu_label">Terms &amp; conditions</span>
          <span className="profile_menu_chevron" aria-hidden="true">
            ›
          </span>
        </button>
        <button
          type="button"
          className="profile_menu_item"
          onClick={() => navigate("/privacypolicies")}
        >
          <span className="profile_menu_label">Privacy policy</span>
          <span className="profile_menu_chevron" aria-hidden="true">
            ›
          </span>
        </button>
      </div>

      <div className="profile_section profile_actions">
        <button
          type="button"
          className="profile_secondary_btn"
          onClick={() => logout()}
        >
          Log Out
        </button>
      </div>
    </div>
  );
};

export default MyProfile;
